import { ApolloClient, InMemoryCache, HttpLink, from, Observable } from '@apollo/client'
import { config } from 'config'
import { authApi } from 'services/Auth'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'

const httpLink = new HttpLink({ uri: config.API_URL + '/graphql' })

/**
 * Ends the session and sends the user back to sign in.
 *
 * Outside the router, so a plain assign rather than navigate(). Guarded so an
 * already-unauthenticated user sitting on /login is not bounced in a loop.
 */
const endSession = (): void => {
  authApi.logout()

  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login')
  }
}

const authMiddleware = setContext(async (_, { headers }) => {
  const token = await authApi.getAccessToken()
  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  }
})

const errorMiddleware = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`),
    )
  if (networkError) {
    console.log(`[Network error]: ${networkError}`)

    // Only an authentication failure ends the session. Calling logout() on any
    // network error is why this was commented out: it signed people out on
    // every dropped connection. Checking the status keeps that behaviour while
    // still handling the case that matters — a token the server rejects, which
    // otherwise leaves the app rendering pages with no data.
    //
    // 401 and 403 are deliberately not treated alike. 401 says the server does
    // not know who this is, which a fresh token may well fix. 403 says it knows
    // perfectly well and the answer is still no — a permission the account does
    // not hold, or a record belonging to someone else. Signing the user out
    // over that is the wrong remedy and loses their session for no reason, so
    // a 403 is left to surface as an ordinary error. This matters more since
    // the access checks added for SP-314: refusals are now expected, not a
    // sign that the session has gone.
    const status = (networkError as { statusCode?: number }).statusCode

    if (status === 401) {
      // A 401 does not necessarily mean the session is over. Refreshing revokes
      // the access token it replaces, so a request already in flight when that
      // happens comes back 401 through no fault of its own.
      if (!operation.getContext().authRetried && authApi.tokenIsExist()) {
        operation.setContext({ authRetried: true })

        // forward() resumes downstream of this link, so authMiddleware does not
        // run again and the retry would otherwise repeat the very token the
        // server just refused — which is no retry at all, only a delayed
        // logout. Read a token here instead and put it on the operation.
        // getAccessToken() refreshes when needed and is single-flight, so a
        // burst of 401s cannot start more than one refresh.
        return new Observable((observer) => {
          authApi
            .getAccessToken()
            .then((token) => {
              if (!token) {
                endSession()
                observer.error(networkError)

                return
              }

              operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
                headers: { ...headers, Authorization: `Bearer ${token}` },
              }))

              // Errors from this second attempt are delivered straight to the
              // subscriber below rather than back through this link, so the
              // session has to be ended here or a genuinely dead one would
              // never send the user to sign in again.
              forward(operation).subscribe({
                next: (value) => observer.next(value),
                error: (retryError) => {
                  const retryStatus = (retryError as { statusCode?: number })?.statusCode

                  // A fresh token was just refused, so this session really is
                  // finished. A 403 here still means the opposite — known user,
                  // forbidden action — and must not end it.
                  if (retryStatus === 401) {
                    endSession()
                  }

                  observer.error(retryError)
                },
                complete: () => observer.complete(),
              })
            })
            .catch(() => {
              endSession()
              observer.error(networkError)
            })
        })
      }

      endSession()
    }
  }
})

const client = new ApolloClient({
  link: from([authMiddleware, errorMiddleware, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
    },
    query: {
      fetchPolicy: 'network-only',
    },
  },
})

export default client
